import api from './api';

let started = false;

/**
 * Inicializa PushNotifications cuando el dashboard corre dentro de Capacitor.
 * En navegador normal no hace nada.
 */
export async function initNativePush() {
  if (started) return;
  const cap = window.Capacitor;
  if (!cap?.isNativePlatform?.()) return;

  const Push = cap.Plugins?.PushNotifications;
  if (!Push) {
    console.warn('[ACIL Push] Plugin PushNotifications no disponible');
    return;
  }
  started = true;

  // Android 8+ requires a notification channel for reliable visible notifications.
  if ((cap.getPlatform?.() || '') === 'android' && Push.createChannel) {
    try {
      await Push.createChannel({
        id: 'acil_alerts',
        name: 'ACIL Intelligence',
        description: 'Alertas e incidencias de ACIL Intelligence',
        importance: 5,
        visibility: 1,
        vibration: true,
      });
      console.info('[ACIL Push] Canal Android acil_alerts listo');
    } catch (err) {
      console.warn('[ACIL Push] No se pudo crear el canal Android', err);
    }
  }

  const saveToken = async (value) => {
    if (!value) return;
    const payload = {
      token: value,
      platform: cap.getPlatform?.() || 'ios',
      device_name: navigator.userAgent,
    };
    try {
      await api.post('/api/notificaciones/dispositivos', payload);
      localStorage.removeItem('acil_pending_push_token');
      console.info('[ACIL Push] Dispositivo registrado', payload.platform);
    } catch (err) {
      localStorage.setItem('acil_pending_push_token', JSON.stringify(payload));
      console.error('[ACIL Push] No se pudo registrar el dispositivo; se reintentará', err);
    }
  };

  // Si el token llegó antes de que la sesión/API estuviera lista, lo reintentamos.
  try {
    const pending = JSON.parse(localStorage.getItem('acil_pending_push_token') || 'null');
    if (pending?.token) await saveToken(pending.token);
  } catch {
    localStorage.removeItem('acil_pending_push_token');
  }

  await Push.addListener('registration', async ({ value }) => {
    await saveToken(value);
  });

  await Push.addListener('registrationError', (err) => {
    console.error('[ACIL Push] Error de registro', err);
  });

  await Push.addListener('pushNotificationReceived', (notification) => {
    console.info('[ACIL Push] Notificación recibida en primer plano', notification);
  });

  await Push.addListener('pushNotificationActionPerformed', ({ notification }) => {
    const route = notification?.data?.route || '/alertas';
    if (route.startsWith('/')) window.location.assign(route);
  });

  let perm = await Push.checkPermissions();
  if (perm.receive === 'prompt') perm = await Push.requestPermissions();
  if (perm.receive === 'granted') await Push.register();
}


/**
 * Fuerza el registro del dispositivo nativo actual y devuelve un diagnóstico.
 * Útil cuando el permiso existe pero el token aún no quedó guardado en backend.
 */
export async function registerCurrentNativeDevice(timeoutMs = 12000) {
  const cap = window.Capacitor;
  if (!cap?.isNativePlatform?.()) {
    return { ok: false, reason: 'not_native' };
  }

  const Push = cap.Plugins?.PushNotifications;
  if (!Push) {
    return { ok: false, reason: 'plugin_unavailable' };
  }

  let perm = await Push.checkPermissions();
  if (perm.receive === 'prompt') perm = await Push.requestPermissions();
  if (perm.receive !== 'granted') {
    return { ok: false, reason: 'permission_denied' };
  }

  return await new Promise(async (resolve) => {
    let settled = false;
    let regHandle;
    let errHandle;

    const finish = async (result) => {
      if (settled) return;
      settled = true;
      try { await regHandle?.remove?.(); } catch {}
      try { await errHandle?.remove?.(); } catch {}
      resolve(result);
    };

    const timer = setTimeout(() => {
      finish({ ok: false, reason: 'registration_timeout' });
    }, timeoutMs);

    regHandle = await Push.addListener('registration', async ({ value }) => {
      clearTimeout(timer);
      try {
        await api.post('/api/notificaciones/dispositivos', {
          token: value,
          platform: cap.getPlatform?.() || 'unknown',
          device_name: navigator.userAgent,
        });
        localStorage.removeItem('acil_pending_push_token');
        await finish({
          ok: true,
          platform: cap.getPlatform?.() || 'unknown',
          token_suffix: value ? value.slice(-12) : null,
        });
      } catch (err) {
        await finish({
          ok: false,
          reason: 'backend_registration_failed',
          status: err?.response?.status || null,
          detail: err?.response?.data?.detail || err?.message || 'Error desconocido',
        });
      }
    });

    errHandle = await Push.addListener('registrationError', async (err) => {
      clearTimeout(timer);
      await finish({
        ok: false,
        reason: 'native_registration_error',
        detail: err?.error || err?.message || JSON.stringify(err),
      });
    });

    try {
      await Push.register();
    } catch (err) {
      clearTimeout(timer);
      await finish({
        ok: false,
        reason: 'register_call_failed',
        detail: err?.message || String(err),
      });
    }
  });
}
